import * as React from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Typography,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Alert,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RichTextEditor } from './components/RichTextEditor';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { sanitizeTelegramHtml } from './utils';

const API_URL = import.meta.env.VITE_API_URL;

type ProductField =
  | 'product_name'
  | 'product_description'
  | 'product_characteristics'
  | 'product_compose'
  | 'product_price'
  | 'product_quantity'
  | 'extra_field_1'
  | 'extra_field_2'
  | 'extra_field_3';

type ProductCardItem = {
  productCardId: number;
  value: string;
  field: ProductField;
  isActive: boolean | null;
  index: number;
  spaces: number | null;
};

type PropsRow = {
  item: ProductCardItem;
  onChange: (id: number, patch: Partial<ProductCardItem>) => void;
};

const FIELD_OPTIONS: { value: ProductField; label: string }[] = [
  { value: 'product_name', label: 'Назва продукту' },
  { value: 'product_description', label: 'Опис продукту' },
  { value: 'product_characteristics', label: 'Характеристика' },
  { value: 'product_compose', label: 'Склад' },
  { value: 'product_price', label: 'Ціна' },
  { value: 'product_quantity', label: 'Кількість' },
  { value: 'extra_field_1', label: 'Додаткове поле №1' },
  { value: 'extra_field_2', label: 'Додаткове поле №2' },
  { value: 'extra_field_3', label: 'Додаткове поле №3' },
];

function SortableRow({ item, onChange }: PropsRow) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: item.productCardId,
    });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Paper ref={setNodeRef} style={style} sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton {...attributes} {...listeners}>
            <DragIndicatorIcon />
          </IconButton>

          <Typography variant="body2" fontWeight={500}>
            Поле "
            {FIELD_OPTIONS.find((opt) => opt.value === item.field)?.label ??
              item.field}
            "
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={!!item.isActive}
                onChange={(e) =>
                  onChange(item.productCardId, { isActive: e.target.checked })
                }
              />
            }
            label="Відобразити"
          />
        </Stack>

        <RichTextEditor
          value={item.value}
          onChange={(html) => onChange(item.productCardId, { value: html })}
          minRows={2}
        />

        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Відступ
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={item.spaces ?? 0}
            onChange={(_, v) =>
              v !== null && onChange(item.productCardId, { spaces: v })
            }
          >
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <ToggleButton key={n} value={n} sx={{ minWidth: 32, px: 0 }}>
                {n}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Stack>
    </Paper>
  );
}

type ProductCardResponse = {
  data: ProductCardItem[];
};

export default function ModuleProducts() {
  const [items, setItems] = React.useState<ProductCardItem[]>([]);
  const [isMacrosOn, setIsMacrosOn] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    fetch(`${API_URL}/settings`)
      .then((r) => r.json())
      .then((res) => setIsMacrosOn(res.settings.isMacrosOn))
      .catch(() => setIsMacrosOn(false));

    fetch(`${API_URL}/product-card`)
      .then((r) => r.json())
      .then((res: ProductCardResponse) => setItems(res.data));
  }, []);

  const updateItem = async (id: number, patch: Partial<ProductCardItem>) => {
    const newPatch: Partial<ProductCardItem> = { ...patch };

    if (patch.value !== undefined) {
      newPatch.value = sanitizeTelegramHtml(patch.value);
    }

    setItems((prev) =>
      prev.map((i) => (i.productCardId === id ? { ...i, ...newPatch } : i)),
    );

    await fetch(`${API_URL}/product-card/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newPatch),
    });
  };

  const onDragEnd = async (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.productCardId === active.id);
    const newIndex = items.findIndex((i) => i.productCardId === over.id);
    const newItems = arrayMove(items, oldIndex, newIndex);

    setItems(newItems);

    await fetch(`${API_URL}/product-card-reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        order: newItems.map((i, index) => ({ id: i.productCardId, index })),
      }),
    });
  };

  if (isMacrosOn) {
    return (
      <Alert severity={'error'} icon={<CancelIcon />} sx={{ mb: 3, mt: 2 }}>
        <b>Увага:</b> Через використання макросів розділ цих налаштувань
        недоступний. Шоб використовувати ці налаштування вимкніть макроси у{' '}
        <a href="/settings">налаштуваннях</a>.
      </Alert>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Картка продукту
      </Typography>

      <Box display="flex" alignItems="center" mb={2}>
        <Typography>
          Картка продукту користувач бачить коли відкриває конкретний товар. Тут
          можна налаштувати, які поля відображати, їх порядок та відступи між
          ними.
        </Typography>

        <Tooltip
          title={
            <img
              src={`/instruction/28.jpg`}
              alt="inline preview"
              style={{ width: 250, borderRadius: 8 }}
            />
          }
          placement="bottom"
        >
          <IconButton size="small" sx={{ ml: 1 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext
          items={items.map((i) => i.productCardId)}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={2}>
            {items.map((item) => (
              <SortableRow
                key={item.productCardId}
                item={item}
                onChange={updateItem}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
    </Box>
  );
}
